// src/Context/UserContext.tsx
import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import axios from "axios";
import { getAccessToken } from "../Components/Utils/TokenRefresh";

/* =========================
   Types
========================= */

// Define what the response structure looks like
interface FetchResponse {
  data: any;
}

// Progress-related types
interface QuestionProgress {
  questionId: string;
  attempts: number;
  correctAttempts: number;
  lastAttemptCorrect?: boolean;
}

interface UnitProgress {
  unitId: string;
  completed: boolean;
  progress: number;
  gotReward: boolean;
  timesCompleted?: number;
  questions: QuestionProgress[];
  performance?: number;
}

interface SectionProgress {
  sectionId: string;
  completed: boolean;
  progress: number;
  gotReward: boolean;
  timesCompleted?: number;
  units: UnitProgress[];
}

interface SavedPhrase {
  phraseKey: string;
  phrase: string;
  translationEn: string;
  targetLang: string;
  locale?: string;
  scenarioSlug?: string;
  level?: string;
  source?: string;
  createdAt: string;
}

interface RoadmapState {
  enabled: boolean;
  state: any;
  updatedAt: string;
}

interface LanguageProgress {
  scenarioProgress?: any[];
  sectionProgress: SectionProgress[];
  savedPhrases: SavedPhrase[];
  roadmap?: RoadmapState;
  stats?: {
    totalPhrasesSaved: number;
    updatedAt: string;
  };
}

interface MathProgress {
  sectionProgress: SectionProgress[];
  scenarioProgress?: any[];
  roadmap?: RoadmapState;
  stats?: {
    questionsAttempted: number;
    questionsCorrect: number;
    updatedAt: string;
  };
}

/**
 * If you have multiple courses, this is the shape LanguageRoleplayHub expects.
 * Keep it permissive so TS doesn't block you while backend evolves.
 */
type UserCourse = {
  courseId?: string;
  kind?: "language" | "math" | string;
  courseKey?: string;

  label?: string;

  // Language settings live here (preferred)
  language?: {
    targetLang?: string; // e.g. "es"
    locale?: string; // e.g. "es-ES"
    nativeLang?: string; // e.g. "en"; "es" for English for Spanish speakers
    /** Section.subject when it differs from the label ("esl"). */
    contentSubject?: string | null;
    script?: string;
    romanization?: string;
    acceptRomanization?: boolean;

    content?: {
      scenariosEnabled?: boolean;
      roadmapEnabled?: boolean;
    };
  };

  // Math settings (for math courses)
  math?: {
    subject?: string;
    grade?: string;
    curriculum?: string;
    track?: string;
    content?: {
      roadmapEnabled?: boolean;
    };
  };

  // ✅ ADD PROGRESS PROPERTIES
  progressLanguage?: LanguageProgress;
  progressMath?: MathProgress;

  // Timestamps
  enabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

interface UserSettings {
  userType: "student" | "adult";
  subject?: string;
  grade?: string;
  curriculum?: string;

  // legacy UI hint (some places may still read this)
  language?: string; // e.g. "es"

  // website language (UI)
  uiLanguage?: string; // e.g. "en"

  /** The learner's own language ("es" for English for Spanish speakers); the lesson chrome uses it. */
  nativeLang?: string;

  /**
   * NEW: course-driven settings
   * This fixes: Property 'courses' does not exist on type 'UserSettings'
   */
  activeCourseId?: string;
  courses?: UserCourse[];

  /**
   * Legacy / existing learning block.
   * Keep this because your current UserContext already declares it
   * and other files may still rely on it.
   */
  learning?: {
    targetLang?: string;
    locale?: string;
    nativeLang?: string;
    content?: {
      scenariosEnabled?: boolean;
      roadmapEnabled?: boolean;
    };
  };
}

// ✅ Add roles + parentApproval types to match backend
interface UserRoles {
  isStudent?: boolean;
  isAdult?: boolean;
  isAdmin?: boolean;
}

interface ParentApproval {
  parentEmail?: string | null;
  parentEmailSetAt?: string | null;
}

// Optional (handy for UI badges later)
interface EmailAuth {
  emailVerified?: boolean;
  emailVerifiedAt?: string | null;
  pendingEmail?: string | null;
  pendingEmailSetAt?: string | null;
}

interface User {
  _id: string;
  username: string;

  settings?: UserSettings;

  // ✅ From backend
  roles?: UserRoles;
  parentApproval?: ParentApproval;
  emailAuth?: EmailAuth;

  email?: string;

  avatarUrl?: string;

  onboarding?: {
    choseStarter: boolean;
    starterCreatureId: string | null;
    introQuizDone: boolean;
    startedAt?: string;
    completedAt?: string;
  };

  subscription?: {
    subscriptionPlan: string;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    subscriptionStatus?: string;
    subscriptionStartDate?: Date;
    subscriptionEndDate?: Date;
  };

  gameProgress?: {
    creatures: {
      creatureId: string;
      level: number;
    }[];
  };
}

interface UserContextType {
  fetchData: (endpoint: string) => Promise<FetchResponse>;

  user: User | null;
  setUser: (user: User | null) => void;
  refreshUser: () => void;
  userLoading: boolean;
  authError: string | null;
}

/* =========================
   Default Context
========================= */

const defaultContextValue: UserContextType = {
  fetchData: async () => ({ data: {} }),

  user: null,
  setUser: () => {},
  refreshUser: () => {},
  userLoading: true,
  authError: null,
};

// Create context
const UserContext = createContext<UserContextType>(defaultContextValue);

/* =========================
   Provider
========================= */

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Generic fetch function (kept as-is)
  const fetchData = async (endpoint: string): Promise<FetchResponse> => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
    );
    return response.data;
  };

  const refreshUser = async (isRetry = false) => {
    setUserLoading(true);
    if (!isRetry) setAuthError(null);

    const token = await getAccessToken();
    if (!token) {
      setUser(null);
      setUserLoading(false);
      setAuthError("Session expired or not logged in.");
      return;
    }

    try {
      const response = await axios.get<User>(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/me`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setUser(response.data);
      setAuthError(null);
    } catch (error: any) {
      console.error("Error fetching user data:", error);

      const status = error?.response?.status;
      const isAuthError = status === 401 || status === 403;

      if (!isRetry && !isAuthError) {
        // Network / server error — retry once after 2s
        setAuthError("Reconnecting...");
        setTimeout(() => refreshUser(true), 2000);
        return; // keep loading=true while waiting for retry
      }

      setUser(null);
      setUserLoading(false);
      setAuthError("Could not load profile. Tap to retry.");
      return;
    }

    setUserLoading(false);
  };

  useEffect(() => {
    refreshUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: UserContextType = {
    fetchData,

    user,
    setUser,
    refreshUser,
    userLoading,
    authError,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

// Custom hook for using the context
export const useUser = (): UserContextType => useContext(UserContext);

export default UserContext;
